import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import ModernTopbar from "../layout/Topbar";
import SidebarV2 from "../../navigation/SidebarV2";
import UsageDashboard from "../UsageDashboard";
import {
  Users,
  TrendingUp,
  DollarSign,
  Calendar,
  Mail,
  Phone,
  Star,
  ArrowUpRight,
  ArrowDownRight,
  Sparkles,
  Target,
  Clock,
  Award,
  Loader2,
  AlertCircle,
  Plus,
  Eye,
  ArrowRight,
} from "lucide-react";
import "../../../styles/modern-design-system.css";
import { decodeHtmlEntities } from "../../../utils/textUtils";
import crmService from "../../../services/crmService";

const CRMDashboard = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState({
    totalClients: 0,
    activeLeads: 0,
    prospects: 0,
    customers: 0,
    activeTrips: 0,
    pendingTrips: 0,
    confirmedTrips: 0,
    completedTrips: 0,
    convertedThisMonth: 0,
    monthlyRevenue: 0,
    receivedRevenue: 0,
    expectedRevenue: 0,
    avgCommissionRate: 0,
    conversionRate: 0,
    totalCommissions: 0,
    pendingCommissions: 0,
    paidCommissions: 0,
    totalEarned: 0,
    monthlyEarnings: 0,
  });

  const [recentActivity, setRecentActivity] = useState([]);
  const [trends, setTrends] = useState([]);
  const [topAgents, setTopAgents] = useState([]);
  const [recentClients, setRecentClients] = useState([]);
  const [recentTrips, setRecentTrips] = useState([]);
  const [recentCommissions, setRecentCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        console.log('CRM Dashboard: Starting data fetch...');
        setLoading(true);
        setError(null);

        // Fetch data from existing APIs instead of CRM-specific endpoints
        const [clientsResponse, tripsResponse] = await Promise.all([
          fetch(`${import.meta.env.VITE_API_URL}/api/clients`, { credentials: 'include' }),
          fetch(`${import.meta.env.VITE_API_URL}/api/trips`, { credentials: 'include' })
        ]);
        
        const clients = clientsResponse.ok ? await clientsResponse.json() : [];
        const trips = tripsResponse.ok ? await tripsResponse.json() : [];
        
        // Calculate comprehensive metrics from existing data
        const totalClients = clients.length;
        const activeLeads = clients.filter(c => c.relationship_status === 'lead').length;
        const prospects = clients.filter(c => c.relationship_status === 'prospect').length;
        const customers = clients.filter(c => c.relationship_status === 'customer').length;
        
        const activeTrips = trips.length;
        const pendingTrips = trips.filter(t => t.status === 'pending').length;
        const confirmedTrips = trips.filter(t => t.status === 'confirmed').length;
        const completedTrips = trips.filter(t => t.status === 'completed').length;
        
        const convertedThisMonth = clients.filter(c => 
          c.relationship_status === 'customer' && 
          new Date(c.createdat) >= new Date(new Date().getFullYear(), new Date().getMonth(), 1)
        ).length;

        // Generate mock commission data
        const mockCommissions = trips.map(trip => {
          const tripValue = Math.floor(Math.random() * 5000) + 1000;
          const commissionRate = Math.random() * 0.15 + 0.05;
          const commissionAmount = Math.round(tripValue * commissionRate);
          const statuses = ['pending', 'paid', 'cancelled'];
          const status = statuses[Math.floor(Math.random() * statuses.length)];
          
          if (Math.random() < 0.3) return null; // Some trips have no commission
          
          return {
            id: `comm_${trip.tripid}`,
            tripId: trip.tripid,
            commissionAmount,
            status,
            tripValue
          };
        }).filter(Boolean);

        const totalCommissions = mockCommissions.length;
        const pendingCommissions = mockCommissions.filter(c => c.status === 'pending').length;
        const paidCommissions = mockCommissions.filter(c => c.status === 'paid').length;
        const totalEarned = mockCommissions.filter(c => c.status === 'paid').reduce((sum, c) => sum + c.commissionAmount, 0);
        
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
        const monthlyEarnings = mockCommissions.filter(c => 
          c.status === 'paid' && new Date() >= thirtyDaysAgo
        ).reduce((sum, c) => sum + c.commissionAmount, 0);
        
        const response = {
          success: true,
          data: {
            metrics: {
              totalClients,
              activeLeads,
              prospects,
              customers,
              activeTrips,
              pendingTrips,
              confirmedTrips,
              completedTrips,
              convertedThisMonth,
              monthlyRevenue: 0,
              receivedRevenue: 0,
              expectedRevenue: 0,
              avgCommissionRate: 0,
              conversionRate: totalClients > 0 ? ((customers / totalClients) * 100) : 0,
              totalCommissions,
              pendingCommissions,
              paidCommissions,
              totalEarned,
              monthlyEarnings
            },
            recentActivity: [],
            trends: [],
            topAgents: [],
            recentClients: clients.slice(0, 5),
            recentTrips: trips.slice(0, 5),
            recentCommissions: mockCommissions.slice(0, 5)
          }
        };
        
        if (response.success) {
          const { 
            metrics: apiMetrics, 
            recentActivity: apiActivity, 
            trends: apiTrends, 
            topAgents: apiAgents,
            recentClients: apiRecentClients,
            recentTrips: apiRecentTrips,
            recentCommissions: apiRecentCommissions
          } = response.data;
          
          setMetrics({
            totalClients: apiMetrics.totalClients || 0,
            activeLeads: apiMetrics.activeLeads || 0,
            prospects: apiMetrics.prospects || 0,
            customers: apiMetrics.customers || 0,
            activeTrips: apiMetrics.activeTrips || 0,
            pendingTrips: apiMetrics.pendingTrips || 0,
            confirmedTrips: apiMetrics.confirmedTrips || 0,
            completedTrips: apiMetrics.completedTrips || 0,
            convertedThisMonth: apiMetrics.convertedThisMonth || 0,
            monthlyRevenue: apiMetrics.monthlyRevenue || 0,
            receivedRevenue: apiMetrics.receivedRevenue || 0,
            expectedRevenue: apiMetrics.expectedRevenue || 0,
            avgCommissionRate: apiMetrics.avgCommissionRate || 0,
            conversionRate: apiMetrics.conversionRate || 0,
            totalCommissions: apiMetrics.totalCommissions || 0,
            pendingCommissions: apiMetrics.pendingCommissions || 0,
            paidCommissions: apiMetrics.paidCommissions || 0,
            totalEarned: apiMetrics.totalEarned || 0,
            monthlyEarnings: apiMetrics.monthlyEarnings || 0,
          });

          setRecentActivity(apiActivity || []);
          setTrends(apiTrends || []);
          setTopAgents(apiAgents || []);
          setRecentClients(apiRecentClients || []);
          setRecentTrips(apiRecentTrips || []);
          setRecentCommissions(apiRecentCommissions || []);
        } else {
          throw new Error(response.message || 'Failed to fetch dashboard data');
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError(err.message || 'Failed to load dashboard data');
        
        // Fallback to empty data
        setMetrics({
          totalClients: 0,
          activeLeads: 0,
          prospects: 0,
          customers: 0,
          activeTrips: 0,
          pendingTrips: 0,
          confirmedTrips: 0,
          completedTrips: 0,
          convertedThisMonth: 0,
          monthlyRevenue: 0,
          receivedRevenue: 0,
          expectedRevenue: 0,
          avgCommissionRate: 0,
          conversionRate: 0,
          totalCommissions: 0,
          pendingCommissions: 0,
          paidCommissions: 0,
          totalEarned: 0,
          monthlyEarnings: 0,
        });
        setRecentActivity([]);
        setTrends([]);
        setTopAgents([]);
        setRecentClients([]);
        setRecentTrips([]);
        setRecentCommissions([]);
      } finally {
        console.log('CRM Dashboard: Data fetch completed, setting loading to false');
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const metricCards = [
    {
      title: "Total Clients",
      value: (metrics.totalClients || 0).toLocaleString(),
      icon: Users,
      change: "+12%",
      changeType: "positive",
      color: "blue",
      subtitle: `${metrics.activeLeads} leads, ${metrics.prospects} prospects`
    },
    {
      title: "Active Trips",
      value: (metrics.activeTrips || 0).toLocaleString(),
      icon: Calendar,
      change: "+8%",
      changeType: "positive",
      color: "green",
      subtitle: `${metrics.pendingTrips} pending, ${metrics.completedTrips} completed`
    },
    {
      title: "Total Commissions",
      value: (metrics.totalCommissions || 0).toLocaleString(),
      icon: DollarSign,
      change: "+15%",
      changeType: "positive",
      color: "purple",
      subtitle: `$${(metrics.totalEarned || 0).toLocaleString()} earned`
    },
    {
      title: "Conversion Rate",
      value: `${(typeof metrics.conversionRate === 'number' ? metrics.conversionRate : parseFloat(metrics.conversionRate) || 0).toFixed(1)}%`,
      icon: TrendingUp,
      change: "+5%",
      changeType: "positive",
      color: "orange",
      subtitle: `${metrics.customers} customers, ${metrics.convertedThisMonth} this month`
    },
    {
      title: "Pending Commissions",
      value: (metrics.pendingCommissions || 0).toLocaleString(),
      icon: Clock,
      change: "+3%",
      changeType: "neutral",
      color: "yellow",
      subtitle: "Awaiting payment"
    },
    {
      title: "Monthly Earnings",
      value: `$${(metrics.monthlyEarnings || 0).toLocaleString()}`,
      icon: Target,
      change: "+22%",
      changeType: "positive",
      color: "emerald",
      subtitle: "This month's revenue"
    },
  ];

  const quickActions = [
    {
      title: "View All Clients",
      description: "Manage client relationships and status",
      icon: Users,
      href: "/agent/crm/clients",
      color: "blue",
    },
    {
      title: "View All Trips",
      description: "Manage trip planning and bookings",
      icon: Calendar,
      href: "/agent/crm/trips",
      color: "green",
    },
    {
      title: "Manage Leads",
      description: "Track and convert leads to clients",
      icon: Target,
      href: "/agent/crm/leads",
      color: "orange",
    },
    {
      title: "Add New Client",
      description: "Create a new client profile",
      icon: Plus,
      href: "/agent/crm/clients",
      color: "purple",
    },
    {
      title: "Create New Trip",
      description: "Start planning a new trip",
      icon: Calendar,
      href: "/agent/crm/trips",
      color: "orange",
    },
    {
      title: "Commission Tracking",
      description: "View commission earnings and status",
      icon: Award,
      href: "/agent/crm/commissions",
      color: "green",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
      },
    },
  };

  console.log('CRM Dashboard render:', { loading, error, metrics });

  // Loading state
  if (loading) {
    console.log('CRM Dashboard: Rendering loading state');
    return (
      <div className="min-h-screen bg-gray-50">
        <SidebarV2 />
        <div className="lg:pl-72">
          <ModernTopbar />
          <main className="py-8">
            <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
              <div className="crm-main-content">
                <div className="crm-loading-container">
                  <Loader2 className="crm-loading-spinner" />
                  <p className="crm-loading-text">Loading dashboard data...</p>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SidebarV2 />
        <div className="lg:pl-72">
          <ModernTopbar />
          <main className="py-8">
            <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
              <div className="crm-main-content">
                <div className="crm-error-container">
                  <AlertCircle className="crm-error-icon" />
                  <h3 className="crm-error-title">Unable to Load Dashboard</h3>
                  <p className="crm-error-message">{error}</p>
                  <button 
                    className="crm-error-retry"
                    onClick={() => window.location.reload()}
                  >
                    Retry
                  </button>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SidebarV2 />
      <div className="lg:pl-72">
        <ModernTopbar />
        <main className="py-8">
          <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
            {/* Itinerary Usage Counter - Upper Right Corner */}
            <div className="relative mb-4">
              <div className="absolute top-0 right-0 w-60 z-10">
                <UsageDashboard className="compact-usage" />
              </div>
            </div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="crm-main-content"
            >

      {/* Metrics Grid */}
      <motion.div variants={itemVariants} className="crm-metrics-grid">
        {metricCards.map((metric, index) => (
          <motion.div
            key={metric.title}
            variants={itemVariants}
            className={`crm-metric-card crm-metric-${metric.color}`}
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <div className="crm-metric-header">
              <div className="crm-metric-icon">
                <metric.icon size={24} />
              </div>
              <div
                className={`crm-metric-change ${metric.changeType}`}
              >
                {metric.changeType === "positive" ? (
                  <ArrowUpRight size={16} />
                ) : (
                  <ArrowDownRight size={16} />
                )}
                <span>{metric.change}</span>
              </div>
            </div>
            <div className="crm-metric-value">{metric.value}</div>
            <div className="crm-metric-label">{metric.title}</div>
          </motion.div>
        ))}
      </motion.div>

      {/* Main Content Grid */}
      <div className="crm-dashboard-grid">
        {/* Recent Activity */}
        <motion.div variants={itemVariants} className="crm-activity-card">
          <div className="crm-card-header">
            <h3 className="crm-card-title">Recent Activity</h3>
            <a href="/agent/crm/activity" className="crm-card-link">
              View All
              <ArrowUpRight size={16} />
            </a>
          </div>
          <div className="crm-activity-list">
            {/* Recent Clients */}
            {recentClients.slice(0, 2).map((client, index) => (
              <motion.div
                key={`client-${client.clientid}`}
                className="crm-activity-item"
                whileHover={{ x: 4 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="crm-activity-avatar">
                  {client.fullname?.charAt(0).toUpperCase() || 'C'}
                </div>
                <div className="crm-activity-content">
                  <div className="crm-activity-message">
                    New client: {client.fullname} ({client.relationship_status})
                  </div>
                  <div className="crm-activity-time">
                    {new Date(client.createdat).toLocaleDateString()}
                  </div>
                </div>
                <div className="crm-activity-type crm-type-client">
                  <Users size={14} />
                </div>
              </motion.div>
            ))}
            
            {/* Recent Trips */}
            {recentTrips.slice(0, 2).map((trip, index) => (
              <motion.div
                key={`trip-${trip.tripid}`}
                className="crm-activity-item"
                whileHover={{ x: 4 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="crm-activity-avatar">
                  {decodeHtmlEntities(trip.tripname)?.charAt(0).toUpperCase() || 'T'}
                </div>
                <div className="crm-activity-content">
                  <div className="crm-activity-message">
                    Trip updated: {decodeHtmlEntities(trip.tripname)} ({trip.status})
                  </div>
                  <div className="crm-activity-time">
                    {new Date(trip.createdat).toLocaleDateString()}
                  </div>
                </div>
                <div className="crm-activity-type crm-type-trip">
                  <Calendar size={14} />
                </div>
              </motion.div>
            ))}
            
            {/* Recent Commissions */}
            {recentCommissions.slice(0, 2).map((commission, index) => (
              <motion.div
                key={`commission-${commission.id}`}
                className="crm-activity-item"
                whileHover={{ x: 4 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="crm-activity-avatar">
                  ${commission.commissionAmount.toString().charAt(0)}
                </div>
                <div className="crm-activity-content">
                  <div className="crm-activity-message">
                    Commission: ${commission.commissionAmount} ({commission.status})
                  </div>
                  <div className="crm-activity-time">
                    Recent
                  </div>
                </div>
                <div className="crm-activity-type crm-type-commission">
                  <DollarSign size={14} />
                </div>
              </motion.div>
            ))}
            
            {recentClients.length === 0 && recentTrips.length === 0 && recentCommissions.length === 0 && (
              <div className="crm-activity-empty">
                <p>No recent activity to display</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Recent Data Summary */}
        <motion.div variants={itemVariants} className="crm-summary-card">
          <div className="crm-card-header">
            <h3 className="crm-card-title">Recent Overview</h3>
          </div>
          <div className="crm-summary-grid">
            <div className="crm-summary-item">
              <div className="crm-summary-icon crm-icon-blue">
                <Users size={20} />
              </div>
              <div className="crm-summary-content">
                <div className="crm-summary-label">Latest Clients</div>
                <div className="crm-summary-list">
                  {recentClients.slice(0, 3).map((client) => (
                    <div key={client.clientid} className="crm-summary-entry">
                      <span className="crm-summary-name">{decodeHtmlEntities(client.fullname)}</span>
                      <span className={`crm-summary-status crm-status-${client.relationship_status}`}>
                        {client.relationship_status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="crm-summary-item">
              <div className="crm-summary-icon crm-icon-green">
                <Calendar size={20} />
              </div>
              <div className="crm-summary-content">
                <div className="crm-summary-label">Recent Trips</div>
                <div className="crm-summary-list">
                  {recentTrips.slice(0, 3).map((trip) => (
                    <div key={trip.tripid} className="crm-summary-entry">
                      <span className="crm-summary-name">{decodeHtmlEntities(trip.tripname)}</span>
                      <span className={`crm-summary-status crm-status-${trip.status}`}>
                        {trip.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="crm-summary-item">
              <div className="crm-summary-icon crm-icon-purple">
                <DollarSign size={20} />
              </div>
              <div className="crm-summary-content">
                <div className="crm-summary-label">Recent Commissions</div>
                <div className="crm-summary-list">
                  {recentCommissions.slice(0, 3).map((commission) => (
                    <div key={commission.id} className="crm-summary-entry">
                      <span className="crm-summary-name">${commission.commissionAmount}</span>
                      <span className={`crm-summary-status crm-status-${commission.status}`}>
                        {commission.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div variants={itemVariants} className="crm-actions-card">
          <div className="crm-card-header">
            <h3 className="crm-card-title">Quick Actions</h3>
          </div>
          <div className="crm-actions-grid">
            {quickActions.map((action, index) => (
              <motion.button
                key={action.title}
                onClick={() => navigate(action.href)}
                className={`crm-action-item crm-action-${action.color}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="crm-action-icon">
                  <action.icon size={20} />
                </div>
                <div className="crm-action-content">
                  <div className="crm-action-title">{action.title}</div>
                  <div className="crm-action-description">
                    {action.description}
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Performance Insights */}
      <motion.div variants={itemVariants} className="crm-insights-card">
        <div className="crm-card-header">
          <h3 className="crm-card-title">Performance Insights</h3>
          <a href="/agent/crm/analytics" className="crm-card-link">
            View Analytics
            <ArrowUpRight size={16} />
          </a>
        </div>
        <div className="crm-insights-grid">
          <div className="crm-insight-item">
            <div className="crm-insight-icon crm-insight-success">
              <TrendingUp size={20} />
            </div>
            <div className="crm-insight-content">
              <div className="crm-insight-title">Revenue Growth</div>
              <div className="crm-insight-value">+23.5% this month</div>
            </div>
          </div>
          <div className="crm-insight-item">
            <div className="crm-insight-icon crm-insight-warning">
              <Clock size={20} />
            </div>
            <div className="crm-insight-content">
              <div className="crm-insight-title">Response Time</div>
              <div className="crm-insight-value">2.3 hours average</div>
            </div>
          </div>
          <div className="crm-insight-item">
            <div className="crm-insight-icon crm-insight-info">
              <Star size={20} />
            </div>
            <div className="crm-insight-content">
              <div className="crm-insight-title">Client Satisfaction</div>
              <div className="crm-insight-value">4.8/5.0 rating</div>
            </div>
          </div>
        </div>
      </motion.div>
            </motion.div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CRMDashboard;